import {ReactElement, useEffect, useLayoutEffect, useRef, useState} from 'react';
import { Dialog } from "primereact/dialog";
import './survey-preview-modal.css';
import { Brand, Question, SurveyFull } from "../../redux/survey/survey.types";
import plusBlack from "../../assets/icons/Plus-black.svg";
import { useSurveyPreview } from "./useSurveyPreview";
import { Button } from "primereact/button";
import { ReactComponent as CreatePencil } from "../../assets/icons/create-pencil.svg";
import { useModerationCheckMutation, getRtrTag } from "../../redux/survey/survey.endpoints";
import { useDispatch } from 'react-redux';
import { findLocalSurveyErrors } from '../../services/surveys/surveys.helpers';
import { showNotification } from '../../redux/notification/notification.slice';
import { standaloneBaseUrl } from 'src/constants/config';
import BrandSelector from './components/brand-selector';

export interface PreviewSurveyModalProps {
  readonly onHide: () => void;
  readonly isVisible: boolean;
  readonly questions: Question[];
  readonly survey: SurveyFull;
}

// TODO remove dead code

const VERTICAL_PADDING = 32;
const SurveyPreviewModal = ({ onHide, isVisible, survey }: PreviewSurveyModalProps): ReactElement => {
  const [viewMode, setViewMode] = useState<'web' | 'rtr'>('rtr');
  const { innerWidth, innerHeight } = window;
  const surveyPreviewProps = useSurveyPreview(survey.questions);
  const extSurveyId = survey.extSurveyId || '';
  const [rtrTag, setRtrTag] = useState('');
  const [rtrSize, setRtrSize] = useState<'300x250' | '970x250'>('300x250');
  const [surveyBrand, setSurveyBrand] = useState<Brand | null>(survey.brand);
  const { activeQuestionIndex } = surveyPreviewProps;
  const [moderationCheck, { error: moderationError }] = useModerationCheckMutation({ fixedCacheKey: `moderation-check-${survey.extSurveyId}` });
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [questionListHeight, setQuestionListHeight] = useState(0);
  const dispatch = useDispatch();
  const isDraft = survey.surveyStatus === 'draft';

  useEffect(() => {
    moderationError && onHide();
  }, [moderationError]);

  useLayoutEffect(() => {
    updateQuestionListHeight();
  }, [innerHeight]);

  useLayoutEffect(() => {
    !questionListHeight && updateQuestionListHeight();
  }, [activeQuestionIndex, surveyPreviewProps.activeAnswerIds]);

  useEffect(() => {
    async function getData() {
      const data = await getRtrTag(extSurveyId, false, rtrSize) || "Error fetching RTR tag.";
      setRtrTag(data);
    }
    getData();
  }, [surveyBrand, rtrSize]);

  useEffect(() => {
    if (isVisible) {
      setSurveyBrand(survey.brand);
    }
  }, [isVisible, survey.brand]);

  function updateQuestionListHeight(): void {
    if (!containerRef.current || !titleRef.current || !footerRef.current) {
      return;
    }

    setQuestionListHeight(containerRef.current.offsetHeight - titleRef.current.offsetHeight - footerRef.current.offsetHeight - (VERTICAL_PADDING * 2));
  }

  const renderTitle = () => {
    return (
      <div
        className="flex flex-col gap-2"
        ref={titleRef}
      >
        <div className="text-sm text-status-gray">
          Survey preview
        </div>
        <div className="text-2xl mb-4">
          {survey.title}
        </div>
      </div>
    );
  };

  const renderLeftPanelFooter = () => {
    return (
      <div
        className="flex flex-col gap-2"
        ref={footerRef}
      >
        {renderLaunchButtons()}
      </div>
    );
  };

  function renderSidePanel(): ReactElement {
    return (
      <div
        className='flex flex-1 rounded-s-xl bg-gray-101 max-w-full overflow-y-auto'
        ref={containerRef}
      >
        <ul
          className={`flex flex-col gap-2 max-h-full max-w-full w-full px-8 justify-between`}
          style={{ paddingTop: VERTICAL_PADDING, paddingBottom: VERTICAL_PADDING }}
        >
          <div className="flex flex-col gap-2">
            {renderTitle()}
            <div
              className="flex flex-col gap-2 overflow-y-auto"
              style={{ maxHeight: questionListHeight }}
            >
              {
                <BrandSelector
                  brand={surveyBrand}
                  setBrand={setSurveyBrand}
                  survey={survey}
                />
              }
            </div>
          </div>
          {renderLeftPanelFooter()}
        </ul>
      </div>
    );
  }

  function renderClose(): ReactElement {
    const position = innerWidth > 640 ? 'top-4 right-4' : 'top-3 right-3';

    return (
      <div className={`${position} absolute z-10`}>
        <Button
          onClick={onHide}
          className='flex justify-center items-center bg-gray-301 size-8 rounded-full'
        >
          <img
            src={plusBlack}
            alt='close'
            className='rotate-45'
            draggable={false}
          />
        </Button>
      </div>
    );
  }

  const renderViewToggle = () => (
    <div className={`flex left-4 top-4 absolute z-10`}>
      <div className={`flex py-3 px-2 ${viewMode === 'rtr' ? 'border-b border-b-blue-500' : ''}`} >
        <div
          className={`cursor-pointer flex items-center gap-1.5 text-sm md:text-base font-medium ${viewMode === 'rtr' ? 'text-blue-500' : ''}`}
          onClick={() => setViewMode('rtr')}
        >
          RTR Preview
        </div>
      </div>
      <div className={`flex py-3 px-2 ${viewMode === 'web' ? 'border-b border-b-blue-500' : ''}`} >
        <div
          className={`cursor-pointer flex items-center gap-1.5 text-sm md:text-base font-medium ${viewMode === 'web' ? 'text-blue-500' : ''}`}
          onClick={() => setViewMode('web')}
        >
          Web Preview
        </div>
      </div>
    </div>
  );

  const onEditClick = () => {
    onHide();
  };

  const submitSurvey = () => {
    if (!survey.extSurveyId) {
      return;
    }

    const localError = findLocalSurveyErrors(survey);
    if (localError) {
      dispatch(showNotification({ summary: localError, sticky: true, theme: "warning" }));
      return;
    }

    void moderationCheck({ extSurveyId: survey.extSurveyId });
  };

  function renderLaunchButtons(): ReactElement | null {
    if (!isDraft) {
      return null;
    }

    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row gap-3 w-full mt-6">
          <Button
            className="gap-3 py-2 px-5 rounded-full border border-gray-light-10 focus:shadow-none"
            icon={(<CreatePencil fill='text-black' />)}
            label="Edit"
            onClick={onEditClick}
          />
          <Button
            className="flex bg-black text-white rounded-full grow focus:shadow-none"
            iconPos="right"
            label={"Launch"}
            onClick={submitSurvey}
            autoFocus={true}
          />
        </div>
      </div>
    );
  }

  function renderWebSurvey(): ReactElement {
    return (
      <div className="flex justify-center items-center w-full h-full relative z-5">
        <iframe
          key={JSON.stringify(surveyBrand)}
          src={`${standaloneBaseUrl}/?survey-id=${survey.extSurveyId}&preview=true`}
          title="Survey Preview"
          width="100%"
          height="100%"
          style={{ border: 'none', zIndex: 1, paddingTop: '65px' }}
          allowFullScreen
        />
      </div>
    );
  }
  function renderRTRTag(): ReactElement {
    const iframeStyling = `
    <style>
      html, body {
        margin: 0;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(241,243,255);
      }
    </style>`;
    return (
      <div className="flex justify-center items-center w-full h-full relative z-5">
        {renderRTRTagSize()}
        <iframe
          key={`${rtrTag}`}
          srcDoc={iframeStyling + rtrTag}
          title="RTR Preview"
          width="100%"
          height="100%"
          style={{ border: 'none', zIndex: 1, paddingTop: '65px' }}
          allowFullScreen
        />
      </div>
    );
  }

  function renderRTRTagSize(): ReactElement {
    return (
      <div className="flex flex-col gap-2 absolute top-20 left-5 z-10">
        <div className="text-sm text-status-gray">
          Select RTR Size
        </div>
        <div className="flex gap-2">
          <Button
            className={"hover:bg-gray-600 p-1 px-2 rounded-xl text-xs " + (rtrSize === '300x250' ? 'bg-black text-white' : 'bg-white text-black')}
            onClick={() => { setRtrSize('300x250'); }}
          >
            300 x 250
          </Button>
          <Button
            className={"hover:bg-gray-600 p-1 px-2 rounded-xl text-xs " + (rtrSize === '970x250' ? 'bg-black text-white' : 'bg-white text-black')}
            onClick={() => setRtrSize('970x250')}
            title="970 x 250"
          >
            970 x 250
          </Button>
        </div>
      </div>
    );
  }


  function renderSmallModal(): ReactElement {
    if (viewMode === 'web') {
      return (
        <div className='flex h-full w-full justify-around gap-3 items-center'>
          {renderViewToggle()}
          {renderWebSurvey()}
          {renderClose()}
        </div>
      );
    } else {
      return (
        <div className='flex h-full w-full justify-around gap-3 items-center'>
          {renderViewToggle()}
          {renderRTRTag()}
          {renderClose()}
        </div>
      );
    }
  }

  function renderFullModal(): ReactElement {
    if (viewMode === 'web') {
      return (
        <div className='flex h-full'>
          {renderSidePanel()}
          <div className='flex flex-2 flex-col justify-center items-start relative'>
            {renderViewToggle()}
            {renderWebSurvey()}
          </div>
          {renderClose()}
        </div>
      );
    }
    else {
      return (
        <div className='flex h-full'>
          {renderSidePanel()}
          <div className='flex flex-2 flex-col justify-center items-start relative'>
            {renderViewToggle()}
            {renderRTRTag()}
          </div>
          {renderClose()}
        </div>
      );
    }
  }

  function renderDialogContent(): ReactElement {
    if (innerWidth < 640) {
      return renderSmallModal();
    }

    return renderFullModal();
  }

  return (
    <Dialog
      onHide={onHide}
      onShow={updateQuestionListHeight}
      visible={isVisible}
      draggable={false}
      blockScroll
      className={innerWidth > 640 ? 'size-11/12 rounded-xl overflow-hidden' : `p-0 max-h-[98%] max-w-[95%] w-full h-full overflow-hidden`}
      content={renderDialogContent}
    />
  );
};

export default SurveyPreviewModal;
